"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Pause, Play, Star, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInView } from "@/lib/useInView";
import { GOOGLE_PROFILE_URL, GOOGLE_REVIEWS, type GoogleReview } from "@/lib/googleReviews";
import type { Locale } from "@/lib/i18n/translations";

const COPY = {
  de: {
    heading: "Google-Bewertungen",
    stars: "5 von 5 Sternen",
    readMore: "Ganze Bewertung lesen",
    close: "Schliessen",
    pause: "Automatischen Wechsel anhalten",
    play: "Automatischen Wechsel starten",
    show: "Bewertung {n} von {total} zeigen",
    source: "Google-Bewertung",
    allOnGoogle: "Alle Bewertungen auf Google",
    // § 5b Abs. 3 UWG: whoever shows reviews must say whether and how they
    // check that the authors used the service. We do: every review shown
    // here is matched to a couple we accompanied (operator, 27.09.2026). A
    // new review is only added once it is matched — otherwise this sentence
    // would be false. Must stay next to the reviews.
    note: "Echte Bewertungen von Paaren, die wir begleitet haben – jede mit unseren Aufträgen abgeglichen.",
    price: { excellent: "Hervorragender Preis", fair: "Angemessener Preis" },
    months: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  },
  en: {
    heading: "Google reviews",
    stars: "5 out of 5 stars",
    readMore: "Read full review",
    close: "Close",
    pause: "Pause automatic rotation",
    play: "Start automatic rotation",
    show: "Show review {n} of {total}",
    source: "Google review",
    allOnGoogle: "All reviews on Google",
    note: "Genuine reviews (in German) from couples we accompanied – each one checked against our bookings.",
    price: { excellent: "Excellent price", fair: "Fair price" },
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  },
  da: {
    heading: "Google-anmeldelser",
    stars: "5 ud af 5 stjerner",
    readMore: "Læs hele anmeldelsen",
    close: "Luk",
    pause: "Sæt automatisk skift på pause",
    play: "Start automatisk skift",
    show: "Vis anmeldelse {n} af {total}",
    source: "Google-anmeldelse",
    allOnGoogle: "Alle anmeldelser på Google",
    note: "Ægte anmeldelser (på tysk) fra par, vi har hjulpet – hver enkelt tjekket op mod vores bookinger.",
    price: { excellent: "Fremragende pris", fair: "Rimelig pris" },
    months: ["januar", "februar", "marts", "april", "maj", "juni", "juli", "august", "september", "oktober", "november", "december"],
  },
  ar: {
    heading: "تقييمات Google",
    stars: "5 من 5 نجوم",
    readMore: "قراءة التقييم كاملًا",
    close: "إغلاق",
    pause: "إيقاف التبديل التلقائي",
    play: "تشغيل التبديل التلقائي",
    show: "عرض التقييم {n} من {total}",
    source: "تقييم على Google",
    allOnGoogle: "جميع التقييمات على Google",
    note: "تقييمات حقيقية (باللغة الألمانية) من أزواج رافقناهم – وقد طابقنا كلًّا منها مع حجوزاتنا.",
    price: { excellent: "سعر ممتاز", fair: "سعر مناسب" },
    months: ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
  },
  uk: {
    heading: "Відгуки в Google",
    stars: "5 із 5 зірок",
    readMore: "Читати відгук повністю",
    close: "Закрити",
    pause: "Зупинити автоматичне перемикання",
    play: "Увімкнути автоматичне перемикання",
    show: "Показати відгук {n} із {total}",
    source: "Відгук у Google",
    allOnGoogle: "Усі відгуки в Google",
    note: "Справжні відгуки (німецькою мовою) від пар, яких ми супроводжували, — кожен звірено з нашими бронюваннями.",
    price: { excellent: "Чудова ціна", fair: "Справедлива ціна" },
    months: ["січень", "лютий", "березень", "квітень", "травень", "червень", "липень", "серпень", "вересень", "жовтень", "листопад", "грудень"],
  },
  ru: {
    heading: "Отзывы в Google",
    stars: "5 из 5 звёзд",
    readMore: "Читать отзыв полностью",
    close: "Закрыть",
    pause: "Остановить автоматическую прокрутку",
    play: "Запустить автоматическую прокрутку",
    show: "Показать отзыв {n} из {total}",
    source: "Отзыв в Google",
    allOnGoogle: "Все отзывы в Google",
    note: "Настоящие отзывы (на немецком языке) от пар, которых мы сопровождали, — каждый сверен с нашими заказами.",
    price: { excellent: "Отличная цена", fair: "Разумная цена" },
    months: ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
  },
  vi: {
    heading: "Đánh giá trên Google",
    stars: "5 trên 5 sao",
    readMore: "Đọc toàn bộ đánh giá",
    close: "Đóng",
    pause: "Tạm dừng tự động chuyển",
    play: "Bật tự động chuyển",
    show: "Xem đánh giá {n}/{total}",
    source: "Đánh giá trên Google",
    allOnGoogle: "Tất cả đánh giá trên Google",
    note: "Đánh giá thật (bằng tiếng Đức) từ các cặp đôi mà chúng tôi đã đồng hành – mỗi đánh giá đều đã được đối chiếu với các đơn đặt dịch vụ của chúng tôi.",
    price: { excellent: "Giá rất tốt", fair: "Giá hợp lý" },
    months: ["Tháng 1 năm", "Tháng 2 năm", "Tháng 3 năm", "Tháng 4 năm", "Tháng 5 năm", "Tháng 6 năm", "Tháng 7 năm", "Tháng 8 năm", "Tháng 9 năm", "Tháng 10 năm", "Tháng 11 năm", "Tháng 12 năm"],
  },
  es: {
    heading: "Reseñas de Google",
    stars: "5 de 5 estrellas",
    readMore: "Leer la reseña completa",
    close: "Cerrar",
    pause: "Pausar el cambio automático",
    play: "Iniciar el cambio automático",
    show: "Mostrar la reseña {n} de {total}",
    source: "Reseña de Google",
    allOnGoogle: "Todas las reseñas en Google",
    note: "Reseñas reales (en alemán) de parejas a las que hemos acompañado – cada una cotejada con nuestros encargos.",
    price: { excellent: "Precio excelente", fair: "Precio razonable" },
    months: ["enero de", "febrero de", "marzo de", "abril de", "mayo de", "junio de", "julio de", "agosto de", "septiembre de", "octubre de", "noviembre de", "diciembre de"],
  },
} as const;

type Copy = (typeof COPY)[Locale];

/** How long each review stays before the next one fades in. */
const INTERVAL_MS = 7000;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** "Reduce motion" as a subscription, so no effect has to copy it into state. */
function subscribeReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** Google's four-colour "G", naming the source of the reviews. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#FBBC05" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#EA4335" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#34A853" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function Stars({ count, label, className }: { count: number; label: string; className?: string }) {
  return (
    <span role="img" aria-label={label} className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className={cn("fill-[#FBBC04] text-[#FBBC04]", className)} strokeWidth={0} aria-hidden />
      ))}
    </span>
  );
}

function meta(review: GoogleReview, c: Copy) {
  const [year, month] = review.month.split("-").map(Number);
  const date = `${c.months[month - 1]} ${year}`;
  return review.price ? `${date} · ${c.price[review.price]}` : date;
}

function Slide({
  review,
  index,
  active,
  c,
  lang,
  onOpen,
}: {
  review: GoogleReview;
  index: number;
  active: boolean;
  c: Copy;
  lang?: string;
  onOpen: (index: number) => void;
}) {
  const textRef = useRef<HTMLQuoteElement>(null);
  const [clamped, setClamped] = useState(false);

  // Only offer "read full review" where the text is actually cut off.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    // All slides share one grid cell, so the area is as tall as the tallest
    // one and nothing below moves when the next review comes in; shorter ones
    // sit centred in it. The old one fades out first and the new one follows,
    // so two texts never overlap.
    <figure
      aria-hidden={!active}
      inert={!active}
      className={cn(
        "col-start-1 row-start-1 flex flex-col items-center justify-center transition-[opacity,translate] ease-out motion-reduce:translate-y-0 motion-reduce:transition-none",
        active ? "translate-y-0 opacity-100 delay-300 duration-700" : "translate-y-2 opacity-0 duration-300",
      )}
    >
      <Stars count={review.stars} label={c.stars} className="h-4 w-4" />
      <blockquote
        ref={textRef}
        lang={lang}
        dir={lang ? "ltr" : undefined}
        className="mt-5 line-clamp-7 font-display text-[1.08rem] italic leading-[1.6] text-white sm:line-clamp-5 sm:text-[1.4rem] sm:leading-[1.55] lg:line-clamp-6 lg:text-[1.2rem] lg:leading-[1.6]"
      >
        „{review.text.split("\n\n").join(" ")}“
      </blockquote>
      {/* The heading above already names the source; each review only needs
          its date and price rating. */}
      <figcaption className="mt-4 text-[12.5px] text-cream-50/70">{meta(review, c)}</figcaption>
      {/* Reserved in every slide, so the area keeps its height. */}
      <div className="mt-1.5 h-7">
        {clamped && (
          <button
            type="button"
            onClick={() => onOpen(index)}
            // A 44px tap area around the small link; the negative margin
            // keeps the reserved row at its height.
            className="-my-2 inline-flex min-h-11 items-center px-2 text-[12.5px] font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white"
          >
            {c.readMore}
          </button>
        )}
      </div>
    </figure>
  );
}

/**
 * Real Google reviews, one at a time: each stays INTERVAL_MS, then fades out
 * and the next fades in. The dots jump to a review and a swipe moves on. The
 * rotation rests while a mouse is over it, while keyboard focus is inside,
 * while a review is open and under "reduce motion"; the small round button
 * stops and restarts it (WCAG 2.2.2). Long reviews are clamped and open in
 * full in a dialog — the wording is never shortened.
 */
export function GoogleReviews({ locale = "de" }: { locale?: Locale }) {
  const c = COPY[locale] ?? COPY.de;
  const lang = locale === "de" ? undefined : "de";
  const count = GOOGLE_REVIEWS.length;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const touchX = useRef<number | null>(null);
  const reduced = useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION).matches,
    () => false,
  );
  // Off-screen the rotation rests as well.
  const [rootRef, inView] = useInView<HTMLDivElement>();
  const running = playing && inView && !hovered && !focused && openIndex === null && !reduced && count > 1;

  // One timer per shown review, so a manual step restarts the full interval.
  useEffect(() => {
    if (!running) return;
    const id = window.setTimeout(() => setIndex((i) => (i + 1) % count), INTERVAL_MS);
    return () => window.clearTimeout(id);
  }, [running, index, count]);

  if (count === 0) return null;

  const go = (step: number) => setIndex((i) => (i + step + count) % count);
  const open = (i: number) => {
    setOpenIndex(i);
    dialogRef.current?.showModal();
  };
  const openReview = openIndex === null ? null : GOOGLE_REVIEWS[openIndex];

  return (
    <div
      ref={rootRef}
      className="mx-auto flex w-full min-w-0 max-w-3xl flex-col text-center lg:max-w-none"
      // A tap on a phone must not count as hovering, or one tap would stop
      // the rotation for good.
      onPointerEnter={(event) => event.pointerType === "mouse" && setHovered(true)}
      onPointerLeave={(event) => event.pointerType === "mouse" && setHovered(false)}
      // Keyboard focus only: a mouse click on a dot is covered by the hover.
      onFocus={(event) => event.target.matches(":focus-visible") && setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <p className="flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-coral-300">
        <GoogleMark className="h-4 w-4" />
        {c.heading}
      </p>

      <div
        // Announce a review only when the visitor chose it, not on every
        // automatic change.
        aria-live={running ? "off" : "polite"}
        className="mt-7 grid touch-pan-y lg:flex-1 lg:content-center"
        onTouchStart={(event) => {
          touchX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const start = touchX.current;
          const end = event.changedTouches[0]?.clientX;
          touchX.current = null;
          if (start === null || end === undefined || Math.abs(end - start) < 40) return;
          go(end < start ? 1 : -1);
        }}
      >
        {GOOGLE_REVIEWS.map((review, i) => (
          <Slide key={i} review={review} index={i} active={i === index} c={c} lang={lang} onOpen={open} />
        ))}
      </div>

      <div className="mt-2 flex items-center justify-center">
        <div className="flex items-center">
          {GOOGLE_REVIEWS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={c.show.replace("{n}", String(i + 1)).replace("{total}", String(count))}
              aria-current={i === index ? "true" : undefined}
              // Small dot, finger-sized button (it was 28×32px).
              className="group grid h-11 w-9 place-items-center"
            >
              <span
                className={cn(
                  "block h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none",
                  i === index ? "w-5 bg-white" : "w-1.5 bg-white/35 group-hover:bg-white/70",
                )}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPlaying((value) => !value)}
          aria-label={playing ? c.pause : c.play}
          className="group grid h-11 w-11 place-items-center motion-reduce:hidden"
        >
          {/* The icons take no pointer events: swapping the icon under the
              cursor would otherwise swallow the pointerleave, and the rotation
              would stay paused after the mouse has gone. The ring stays 28px,
              the button around it is 44px to tap. */}
          <span className="pointer-events-none grid h-7 w-7 place-items-center rounded-full border border-white/25 text-white/70 transition-colors group-hover:border-white/60 group-hover:text-white">
            {playing ? (
              <Pause className="h-3 w-3" aria-hidden />
            ) : (
              <Play className="h-3 w-3" aria-hidden />
            )}
          </span>
        </button>
      </div>

      <p className="mx-auto mt-6 max-w-2xl text-[11.5px] leading-relaxed text-cream-50/60">
        {c.note}
        {GOOGLE_PROFILE_URL && (
          <>
            {" "}
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap text-cream-50/85 underline underline-offset-2 hover:text-white"
            >
              {c.allOnGoogle}
            </a>
          </>
        )}
      </p>

      <dialog
        ref={dialogRef}
        onClose={() => setOpenIndex(null)}
        // A click on the backdrop lands on the dialog element itself.
        onClick={(event) => {
          if (event.target === dialogRef.current) dialogRef.current?.close();
        }}
        aria-label={c.source}
        className="m-auto w-[min(34rem,calc(100vw-2rem))] rounded-2xl border-0 bg-white p-0 text-start outline-none backdrop:bg-navy-950/60"
      >
        {openReview && (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <GoogleMark className="h-5 w-5" />
                <Stars count={openReview.stars} label={c.stars} className="h-[15px] w-[15px]" />
              </div>
              <button
                type="button"
                onClick={() => dialogRef.current?.close()}
                aria-label={c.close}
                className="-m-2 grid h-10 w-10 place-items-center rounded-full text-navy-600 transition-colors hover:bg-navy-50 hover:text-navy-900"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <p className="mt-2 text-[12.5px] text-navy-500">
              {c.source} · {meta(openReview, c)}
            </p>
            <div lang={lang} dir={lang ? "ltr" : undefined} className="mt-4 space-y-3 text-[15px] leading-[1.65] text-navy-800">
              {openReview.text.split("\n\n").map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
}
